import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FeatureFlagService } from '@cudirect/shared-ui-library';
import { IEconsentAcknowledgment, ILegalConsent } from '@workflow/ecp-workflow/dynamic-app/types/legal-consent.type';
import { BuildingBlock } from '../building-block.types';
import { CheckboxComponent } from '@workflow/dynamic-render/building-block/controls/checkbox/checkbox.component';
import { AddressService } from '@shared/service/address.service';
import { LogicService } from '@workflow/service/logic/logic.service';
import { StringVariableService } from '@workflow/service/string-variable/string-variable.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { StatefulService } from '@app/service/stateful.service';

@BuildingBlock('econsent')
@Component({
  selector: 'app-econsent',
  templateUrl: './econsent.component.html',
  standalone: false
})
export class EconsentComponent extends CheckboxComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** Variable to hold Cosent detail */
  public allConsents: IEconsentAcknowledgment[];

  /** Variable to hold Cosent either EConsent / TCPA */
  public consent: IEconsentAcknowledgment;

  /** Variable to hold Additional Cosent detail */
  public additionalConsents: IEconsentAcknowledgment[];

  /** Variable to hold Consent Library */
  public consentLibrary: ILegalConsent[] = [];

  /** variable to hold Consent Type */
  public consentType: string;

  public isPageSubmitted: boolean;

  constructor(
    logicService: LogicService,
    changeDetectorRef: ChangeDetectorRef,
    stringVariableService: StringVariableService,
    addressService: AddressService,
    public readonly featureFlagService: FeatureFlagService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    public readonly dynamicPageService: WorkflowPageNavigationService,
    public readonly applicationApiService: ApplicationApiService,
    private readonly stateManager: StateManagementService
  ) {
    super(logicService, changeDetectorRef, stringVariableService, addressService, featureFlagService);
  }

  ngOnInit(): void {
    /** Make the tcpa value as false when bundle value is null. This is because to implement Opt-Out service logic
     * This is only for Account opening TCPA alone
     */
    if (this.field.data?.protocol === 'api' && this.field.config?.payload?.consentType === 'tcpa') {
      const currentValue = this.field.data.value;
      if (currentValue === null) {
        this.field.data.value = false;
      }
    }

    /** When user submit the page, focus the first invalid field */
    this.pageSubmitted$.subscribe((value) => {
      this.isPageSubmitted = value;
    });

    if (this.field?.config?.payload) {
      this.consentType = this.field.config.payload?.consentType?.toString()?.toLowerCase();
      const consentRootPath = this.field.config.payload?.consentRootPath.toString();

      this.consentLibrary = this.adminPortalFeaturesService.config.consentManagement.consentLibrary;

      switch (this.consentType) {
        case 'econsent':
          this.consent = this.adminPortalFeaturesService.config.consentManagement?.primaryAcknowledgement;
          this.consent.consentRootPath = consentRootPath;
          break;
        case 'tcpa':
          this.consent = this.adminPortalFeaturesService.config.consentManagement?.tcpaAcknowledgement;
          this.consent.consentRootPath = consentRootPath;
          break;
        case 'additionaleconsent':
          this.additionalConsents = this.adminPortalFeaturesService.config.consentManagement?.additionalAcknowledgements;
          this.additionalConsents.forEach((item, index) => {
            item.consentRootPath = consentRootPath + '/' + index;
            setTimeout(() => {
              const key = (item.consentRootPath + '/isAccepted')
                .replace('$', this.field.iterationId?.toString())
                .replace('#', this.field.pageIterationId?.toString());
              /**
               * if you want to know why I am using this to seed the checkboxes instead of
               * stateful value - it is because stateful value seems to hold on to the previous array
               * longer than it should, resulting in getting a value from a previous application, even with a timeout
               * it results in getting true, when the actual value was null (verified via network calls and debugging)
               * by using the StateManager, we are only working against the entire object, so what we see more closely matches
               * the network calls. StatefulValue and race conditions... Why did I expect anything else?
               */
              item.isAccepted = this.stateManager.getValue<boolean>(key) ?? false;
              // item.isAccepted = Boolean(
              //   this.statefulService.wrap(item.consentRootPath + '/isAccepted', this.field.iterationId, this.field.pageIterationId).value
              // );
            }, 50);
          });
          break;
        default:
          break;
      }
    }
  }

  /**
   * capture consent content
   */
  public captureContent(selectedConsent: IEconsentAcknowledgment) {
    const setDefaultBusinessPageIterationId = selectedConsent.consentRootPath.includes('#') && this.field?.pageIterationId === undefined;
    const pageIterationId = setDefaultBusinessPageIterationId ? 0 : this.field?.pageIterationId;
    const snapshot = this.statefulService.wrap(selectedConsent.consentRootPath + '/acceptanceSnapshotReferenceId', null, pageIterationId);
    let snapShotId: string = snapshot.value as string;
    const protocol = snapshot.protocol;
    if (!snapShotId) {
      snapShotId = crypto.randomUUID();
      snapshot.value = snapShotId;
    }
    // check if currently coapplicant - we only want to add to user for primary
    let coapp = false;
    if (
      selectedConsent.consentRootPath.includes('coBorrowers') ||
      selectedConsent.consentRootPath.includes('coApplicant') ||
      selectedConsent.consentRootPath.includes('additionalCoApplicant')
    ) {
      coapp = true;
    }
    const snapshotSave = () => {
      this.applicationApiService.captureConsent(protocol, {
        acknowledgementId: selectedConsent.acknowledgeId,
        acceptenceId: snapShotId,
        isCoApplicant: coapp
      });
      snapshot.unsubscribe(snapshotSave);
    };
    snapshot.afterPatch(snapshotSave);
  }
}
